import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import type { Workspace } from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import { canEnter } from "../auth/permissions";

// The two halves of the building, in the order the product leads with them. This is event
// management first: the organisation runs its own programme, and lets the hall out around it. So
// somebody who holds both sides opens in events rather than being asked which they meant.
const order: Workspace[] = ["events", "bookings"];

// A browser can refuse storage outright: private modes, blocked contexts and quota failures throw
// on access rather than returning null. Which half of the building somebody was last looking at is
// a preference, not a requirement, so a refusal falls back to the default instead of taking the
// application down. Same reasoning, and the same shape, as the appearance choice.
function readStoredWorkspace(): Workspace | null {
  try {
    const stored = window.localStorage.getItem("venueops-workspace");
    return stored === "events" || stored === "bookings" ? stored : null;
  } catch {
    return null;
  }
}

function writeStoredWorkspace(workspace: Workspace): void {
  try {
    window.localStorage.setItem("venueops-workspace", workspace);
  } catch {
    // The choice still applies to this session; it just will not survive a reload.
  }
}

interface WorkspaceChoice {
  choice: Workspace | null;
  choose(workspace: Workspace): void;
}

// Only the choice is held here, never the answer. What somebody may enter is decided by the two
// roles on their membership, which the server owns and can change under a session that is still
// open, so the answer is worked out from the membership on every read instead of being stored
// alongside the preference where the two could disagree.
const WorkspaceChoiceContext = createContext<WorkspaceChoice>({
  choice: null,
  choose: () => undefined,
});

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const [choice, setChoice] = useState<Workspace | null>(readStoredWorkspace);
  const choose = useCallback((workspace: Workspace) => {
    setChoice(workspace);
    writeStoredWorkspace(workspace);
  }, []);
  const value = useMemo(() => ({ choice, choose }), [choice, choose]);
  return (
    <WorkspaceChoiceContext.Provider value={value}>{children}</WorkspaceChoiceContext.Provider>
  );
}

export interface WorkspaceState {
  // The half being looked at. Always one this person may actually enter.
  workspace: Workspace;
  // Both halves for somebody who holds both, one for somebody who holds one. A membership with no
  // role on either side cannot exist, so this is empty only when there is no membership at all,
  // which is the platform operator looking at a tenant workspace they are not a member of.
  available: Workspace[];
  setWorkspace(workspace: Workspace): void;
}

// The clamp is the point of this hook. A stored choice outlives the authority that allowed it: a
// person moved to events only still has "bookings" in their browser, and honoring it would open a
// workspace whose every request the server refuses. Reading the roles each time means losing a side
// takes effect on the next render rather than on the next time somebody clears their storage.
export function useWorkspace(): WorkspaceState {
  const auth = useAuth();
  const { choice, choose } = useContext(WorkspaceChoiceContext);
  const membership = auth.session?.selectedMembership;
  const available = order.filter((workspace) => canEnter(membership, workspace));
  const workspace = choice && available.includes(choice) ? choice : (available[0] ?? "events");
  return { workspace, available, setWorkspace: choose };
}
